"use client"

import { Card } from "@/components/ui/card"
import { motion } from "framer-motion"
import { CheckCircle, MapPin, User, Zap, Star } from "lucide-react"
import { useQuoteForm } from "@/hooks/use-quote-form"
import { FormStepIndicator } from "./form-step-indicator"
import { StepMoveDetails } from "./step-move-details"
import { StepContactInfo } from "./step-contact-info"
import { StepFinalDetails } from "./step-final-details"
import { FormNavigation } from "./form-navigation"
import { SuccessMessage } from "./success-message"
import { ErrorAlert } from "./error-alert"
import { TrustFeatures } from "./trust-features"

const steps = [
  { title: "Move Details", icon: MapPin },
  { title: "Contact Info", icon: User },
  { title: "Final Details", icon: CheckCircle },
]

const containerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, staggerChildren: 0.1 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
}

export function QuoteForm() {
  const {
    form,
    currentStep,
    isSubmitted,
    submitError,
    handleNext,
    handlePrevious,
    onSubmit,
    isStepValid,
  } = useQuoteForm()

  const formData = form.watch()

  return (
    <motion.section
      className="py-16 sm:py-24 bg-gradient-to-br from-slate-50 via-blue-50/30 to-orange-50/20 relative overflow-hidden"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      id="quote-form"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#003DA5] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#FF7A00] rounded-full blur-3xl"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-4 h-4 bg-[#FF7A00]/30 rounded-full"
          animate={{ y: [0, -20, 0], opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-40 right-20 w-6 h-6 bg-[#003DA5]/20 rounded-full"
          animate={{ y: [0, 20, 0], opacity: [0.2, 0.6, 0.2] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        <motion.div
          className="absolute bottom-32 right-1/3 w-3 h-3 bg-[#FF7A00]/40 rounded-full"
          animate={{ y: [0, -15, 0], opacity: [0.4, 0.9, 0.4] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
      </div>
      
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div className="text-center mb-12 sm:mb-16" variants={itemVariants}>
          <motion.div
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF7A00]/10 to-[#003DA5]/10 backdrop-blur-sm px-4 py-2 rounded-full border border-[#FF7A00]/20 text-sm font-medium text-[#FF7A00] mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Zap className="w-4 h-4" />
            Get Instant Quote
          </motion.div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
            Start Your{" "}
            <span className="bg-gradient-to-r from-[#FF7A00] to-[#003DA5] bg-clip-text text-transparent">
              Moving Journey
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Fill in a few details and get your personalized moving quote in seconds
          </p>
        </motion.div>

        <Card className="relative p-8 sm:p-10 lg:p-12 bg-white/90 backdrop-blur-sm border-2 border-white/50 shadow-2xl hover:shadow-3xl transition-all duration-500 rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#003DA5]/5 via-transparent to-[#FF7A00]/5 opacity-50"></div>
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#003DA5]/20 via-[#FF7A00]/20 to-[#003DA5]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
          
          <div className="relative z-10">
            <FormStepIndicator steps={steps} currentStep={currentStep} />

            {isSubmitted ? (
              <SuccessMessage phone={formData.phone} />
            ) : (
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">

                {submitError && <ErrorAlert message={submitError} />}

                <div>
                  {currentStep === 0 && (
                    <StepMoveDetails 
                      control={form.control} 
                      errors={form.formState.errors} 
                      trigger={form.trigger}
                    />
                  )}

                  {currentStep === 1 && (
                    <StepContactInfo control={form.control} errors={form.formState.errors} />
                  )}

                  {currentStep === 2 && (
                    <StepFinalDetails 
                      control={form.control} 
                      errors={form.formState.errors}
                      formData={formData}
                    />
                  )}
                </div>

                <FormNavigation
                  currentStep={currentStep}
                  totalSteps={steps.length}
                  isSubmitting={form.formState.isSubmitting}
                  isStepValid={isStepValid(currentStep)}
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                  onSubmit={form.handleSubmit(onSubmit)}
                />
              </form>
            )}
          </div>
        </Card>

        <TrustFeatures variants={itemVariants} />

        {/* Bottom Stats */}
        <motion.div className="text-center mt-12" variants={itemVariants}>
          <div className="flex justify-center items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-500 fill-current" />
              <span>4.8/5 Rating</span>
            </div>
            <div className="w-px h-4 bg-slate-300"></div>
            <div>500+ Happy Customers</div>
            <div className="w-px h-4 bg-slate-300"></div>
            <div>Free Quote</div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  )
}
